/**
 * Organizations Tab
 *
 * CRUD operations for organizations.
 * Lists all organizations with member and vessel counts.
 */

import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  Users,
  Ship,
  Loader2,
  AlertCircle,
  Eye
} from 'lucide-react';

import { useAdminOrganizations } from '../../../hooks/admin/useAdminOrganizations';
import { OrganizationOnboardingWizard, TempPasswordModal } from './OrganizationOnboardingWizard';
import { EditOrganizationModal } from './EditOrganizationModal';

const OrganizationsTab = () => {
  const {
    organizations,
    isLoading,
    error,
    createOrganization,
    updateOrganization,
    deleteOrganization
  } = useAdminOrganizations();

  const [showWizard, setShowWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Track org to delete
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [reactivateSuccess, setReactivateSuccess] = useState(false);
  const [createdOrgPassword, setCreatedOrgPassword] = useState(null); // { orgName, ownerEmail, tempPassword }

  const handleOpenWizard = () => {
    setShowWizard(true);
  };

  const handleWizardSuccess = (data) => {
    // Close wizard — list already refreshed inside the hook's createOrganization
    setShowWizard(false);
    // Surface temp password in parent-owned modal so it survives the wizard unmounting
    if (data?.tempPassword) {
      setCreatedOrgPassword({
        orgName: data.organization?.name || '',
        ownerEmail: data.owner?.email || '',
        tempPassword: data.tempPassword
      });
    }
  };

  const handleOpenEdit = (org) => {
    setEditingOrg(org);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (orgId, updates) => {
    const result = await updateOrganization(orgId, updates);
    if (!result) throw new Error('Failed to save organisation');
    setShowEditModal(false);
    setEditingOrg(null);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleDelete = async (orgId) => {
    console.log('[OrganizationsTab] Delete requested for orgId:', orgId);
    const success = await deleteOrganization(orgId);
    console.log('[OrganizationsTab] Delete result:', success);

    if (success) {
      setShowEditModal(false);
      setEditingOrg(null);
      setDeleteConfirm(null);
    } else {
      console.error('[OrganizationsTab] Delete failed - check error state');
      // Keep dialog open so user can see error or try again
    }
  };

  const handleReactivate = async (orgId) => {
    const result = await updateOrganization(orgId, { active: true });
    if (!result) throw new Error('Failed to reactivate organisation');
    setShowEditModal(false);
    setEditingOrg(null);
    setReactivateSuccess(true);
    setTimeout(() => setReactivateSuccess(false), 3000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Save success banner */}
      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium">
          Organisation saved successfully.
        </div>
      )}

      {/* Reactivate success banner */}
      {reactivateSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium">
          Organisation reactivated successfully.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Organizations</h2>
          <p className="text-sm text-slate-500">
            {organizations.length} organization{organizations.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          onClick={handleOpenWizard}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Organization
        </button>
      </div>

      {/* Organizations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Organization
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Members
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Vessels
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {organizations.map((org) => (
              <tr key={org.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{org.name}</div>
                      <div className="text-xs text-slate-500">ID: {org.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <code className="text-sm bg-slate-100 px-2 py-1 rounded">{org.slug}</code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    org.tier === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                    org.tier === 'professional' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {org.tier || 'standard'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-slate-600">
                    <Users className="h-4 w-4" />
                    <span>{org.memberCount || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-slate-600">
                    <Ship className="h-4 w-4" />
                    <span>{org.vesselCount || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button
                    onClick={() => handleOpenEdit(org)}
                    className="p-2 text-slate-400 hover:text-cyan-600 transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(org)}
                    className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                  No organizations yet. Create your first organization to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Onboarding Wizard */}
      {/* FT-105: Pass createOrganization as onSubmit to avoid deprecated Supabase API */}
      <OrganizationOnboardingWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={handleWizardSuccess}
        onSubmit={createOrganization}
      />

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        isOpen={showEditModal}
        organization={editingOrg}
        onClose={() => {
          setShowEditModal(false);
          setEditingOrg(null);
        }}
        onSave={handleSaveEdit}
        onDelete={handleDelete}
        onReactivate={handleReactivate}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Deactivate Organization?
                </h3>
                {/* FT-460: the server DELETE /admin/organizations/:id is a SOFT
                    delete — it sets the organisation to active:false and leaves
                    its members and vessels untouched. Copy must reflect that: no
                    permanent/irreversible deletion of members or vessels. */}
                <p className="text-slate-600 mb-4">
                  Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>?
                </p>
                <ul className="text-sm text-slate-600 space-y-1 mb-6">
                  <li>• The organization is deactivated and hidden from active lists</li>
                  <li>• Its members ({deleteConfirm.member_count || 0}) can no longer sign in</li>
                  <li>• Its members and vessels ({deleteConfirm.vessel_count || 0}) are retained, not deleted</li>
                </ul>
                <p className="text-sm font-medium text-slate-600">
                  This can be reversed by reactivating the organization later.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors font-medium"
              >
                Deactivate Organization
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temp password modal — rendered here (not in wizard) so it survives the
          wizard unmounting during the hook's fetchOrganizations isLoading flash */}
      {createdOrgPassword && (
        <TempPasswordModal
          orgName={createdOrgPassword.orgName}
          ownerEmail={createdOrgPassword.ownerEmail}
          tempPassword={createdOrgPassword.tempPassword}
          onClose={() => setCreatedOrgPassword(null)}
        />
      )}
    </div>
  );
};

export default OrganizationsTab;
